import type { INodeDto } from '@/types/INodeDto';
import { Model } from '../../api/apiClient';
import { InstanceTree } from './InstanceTree';

/**
 * Represents a model instance containing both the Model metadata
 * and the InstanceTree structure for node (INodeDto) objects.
 */
export class InstanceModel {
  /** The Model metadata from the API */
  readonly model: Model;

  /** The tree structure of nodes for this model */
  readonly instanceTree: InstanceTree;

  /** Reference to the shared node array */
  private nodes: INodeDto[];

  /**
   * Creates a new InstanceModel.
   * @param model The Model metadata from the API
   * @param nodes The shared array of nodes (INodeDto; may contain nodes from multiple models)
   */
  constructor(model: Model, nodes: INodeDto[]) {
    const modelId = model.modelId;
    if (!modelId) {
      throw new Error('Model must have a modelId');
    }

    this.model = model;
    this.nodes = nodes;
    this.instanceTree = new InstanceTree(modelId, nodes);
  }

  /**
   * Populates the InstanceTree from the node array.
   * Call this after the node array has been loaded with data.
   */
  buildTree(): void {
    this.instanceTree.buildTree();
  }

  /**
   * Gets the modelId for this instance.
   */
  get modelId(): string {
    return this.model.modelId!;
  }

  /**
   * Gets the display name for this model.
   */
  get displayName(): string | undefined {
    return this.model.apsItemName;
  }

  /**
   * Gets the number of nodes in this model.
   */
  get nodeCount(): number {
    return this.instanceTree.getNodeCount();
  }

  /**
   * Gets a node by its index in the shared array.
   */
  getNode(index: number): INodeDto | null {
    return this.instanceTree.getNode(index);
  }

  /**
   * Gets a node by its nodeId.
   */
  getNodeByNodeId(nodeId: bigint): INodeDto | null {
    const index = this.instanceTree.getIndexByNodeId(nodeId);
    if (index !== undefined) {
      return this.instanceTree.getNode(index);
    }
    return null;
  }

  /**
   * Gets all root nodes (nodes without parents) for this model.
   */
  getRootNodes(): INodeDto[] {
    const roots: INodeDto[] = [];
    for (const index of this.instanceTree.getRootIndices()) {
      const node = this.instanceTree.getNode(index);
      if (node) {
        roots.push(node);
      }
    }
    return roots;
  }

  /**
   * Gets all nodes belonging to this model.
   */
  getAllNodes(): INodeDto[] {
    const nodes: INodeDto[] = [];
    for (const index of this.instanceTree.getNodeIndices()) {
      const node = this.instanceTree.getNode(index);
      if (node) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  /**
   * Gets the children of a node by its index.
   */
  getChildren(index: number): INodeDto[] {
    const children: INodeDto[] = [];
    for (const childIndex of this.instanceTree.getChildIndices(index)) {
      const node = this.instanceTree.getNode(childIndex);
      if (node) {
        children.push(node);
      }
    }
    return children;
  }

  /**
   * Gets the parent of a node by its index.
   */
  getParent(index: number): INodeDto | null {
    const parentIndex = this.instanceTree.getParentIndex(index);
    if (parentIndex === -1) {
      return null;
    }
    return this.instanceTree.getNode(parentIndex);
  }

  /**
   * Traverses all nodes in the model depth-first.
   */
  traverseDepthFirst(callback: (node: INodeDto, index: number, depth: number) => void): void {
    this.instanceTree.traverseDepthFirst((index, depth) => {
      const node = this.instanceTree.getNode(index);
      if (node) {
        callback(node, index, depth);
      }
    });
  }

  /**
   * Traverses all nodes in the model breadth-first.
   */
  traverseBreadthFirst(callback: (node: INodeDto, index: number, depth: number) => void): void {
    this.instanceTree.traverseBreadthFirst((index, depth) => {
      const node = this.instanceTree.getNode(index);
      if (node) {
        callback(node, index, depth);
      }
    });
  }
}

